<template>
  <v-data-table-server
    class="white"
    v-model:expanded="expanded"
    :items="items"
    item-value="tableItemId"
    :show-expand="true"
    :hide-default-header="true"
    :items-per-page="-1"
    :items-length="0"
    color="primary"
    :group-by="[{ key: 'osType', order: 'asc' }]"
    :group-desc="false"
  >
    <template #headers>
      <tr>
        <th class="w-5" :aria-label="$t('ariaLabels.expanderColumn')"></th>
        <th class="w-50">
          {{ $t("devicesSettings.devicePostureTab.vulnerability") }}
        </th>
        <th class="pr-6">
          {{ $t("devicesSettings.devicePostureTab.policies") }}
        </th>
        <th class="pr-6 w-15">
          {{ $t("devicesSettings.devicePostureTab.action") }}
        </th>
        <th :aria-label="$t('ariaLabels.actionsColumn')"></th>
      </tr>
    </template>
    <template v-slot:group-header="{ item, toggleGroup }">
      <tr>
        <th class="group-header pl-0" colspan="5">
          <v-btn
            class="d-none"
            :aria-label="$t('ariaLabels.expanderButton')"
            :ref="`${item.items[0].raw.osType}ExpandButton`"
            @click="toggleGroup(item)"
          ></v-btn>
          <v-btn
            :aria-label="$t('ariaLabels.expanderButton')"
            :icon="true"
            variant="plain"
            :ripple="false"
            class="toggle-expander group-expand-icon"
            @click="expandGroup(item.items as DevicePostureItemsTuple)"
          >
            <v-icon
              :class="{ rotated: isGroupExpanded(item.items as DevicePostureItemsTuple) }"
              icon="$chevronDown"
            >
            </v-icon>
          </v-btn>
          <span class="subtitle1">
            {{ $t(`devicesSettings.devicePostureTab.osTypes.${item.items[0].raw.osType}`) }}
          </span>
        </th>
      </tr>
    </template>

    <template #item="{ item, toggleExpand, isExpanded, internalItem }">
      <tr>
        <td class="w-5">
          <v-icon
            class="mr-2 cursor-pointer"
            icon="$dropdown"
            color="primary"
            v-if="showExpandGroup(item)"
            :class="{ rotated: isExpanded(internalItem) }"
            @click="toggleExpand(internalItem)"
          />
        </td>
        <td class="pl-0">
          <div class="d-flex align-center">
            <v-icon size="40" class="mr-4" :icon="getPolicyTypeIcon(item.policyType)"> </v-icon>
            <span class="subtitle1">{{
              $t(`devicesSettings.devicePostureTab.posturePolicies.${item.policyType}`)
            }}</span>
          </div>
        </td>
        <td>
          <div v-if="item.policiesQuantity" class="body2">
            {{
              $t(
                "devicesSettings.devicePostureTab.policiesQuantity",
                {
                  n: item.policiesQuantity,
                },
                item.policiesQuantity
              )
            }}
          </div>
          <div v-else class="body2 text--grey">
            {{ $t("devicesSettings.devicePostureTab.noPolicies") }}
          </div>
        </td>
        <td class="w-5 pr-1"></td>
        <td class="w-5 pr-1"></td>
      </tr>
    </template>
    <template v-slot:expanded-row="{ item }">
      <template v-for="policy in item.policies" :key="policy.id">
        <tr v-if="policy.labels?.length" :key="policy.id">
          <td class="w-5"></td>
          <td
            :colspan="2"
            class="pl-4 pr-5 py-4 word-break-all white-space-normal expandable-content"
          >
            <div class="pl-10 d-flex flex-wrap label-container">
              <v-chip
                v-for="label in policy.labels"
                :key="label.id"
                variant="flat"
                rounded="sm"
                size="default"
                :color="label.color"
                class="label-item h-100 py-0 px-1 flex-shrink-0"
              >
                <span class="coro-label text-uppercase text-primary">
                  {{ label.name.replace(/ /g, "&nbsp;") }}
                </span>
              </v-chip>
            </div>
          </td>
          <td class="pl-4">
            <span class="body2" :class="getPostureTextClass(policy)">
              {{ policy.displayValue }}
            </span>
          </td>
          <td>
            <div class="d-flex">
              <v-menu bottom left>
                <template v-slot:activator="{ props }">
                  <v-icon icon="$dots" class="item-clickable" v-bind="props"></v-icon>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(action, index) in actions"
                    :key="index"
                    :value="index"
                    @click="onActionClick(action, policy, item)"
                  >
                    <v-list-item-title>
                      {{ $t(`devicesSettings.devicePostureTab.actions.${action}`) }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </td>
        </tr>
      </template>
    </template>
    <template v-slot:bottom></template>
  </v-data-table-server>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType, onMounted } from "vue";
import difference from "lodash/difference";
import union from "lodash/union";
import {
  DevicePostureAction,
  DevicePostureChoice,
  DevicePostureSettings,
  DeviceVulnerability,
} from "@/constants/devices";
import type {
  DevicePostureAdaptedItem,
  DevicePostureCombinedType,
  DevicePostureRegularItem,
} from "@/_store/device-posture/devices-posture-settings.module";

type DevicePostureItemsTuple = [{ raw: DevicePostureAdaptedItem }];

export default defineComponent({
  name: "EndpointSecurityDevicePostureTable",
  props: {
    items: {
      type: Array as PropType<DevicePostureAdaptedItem[]>,
      required: true,
    },
    actions: {
      type: Array as PropType<DevicePostureAction[]>,
      default: () => [DevicePostureAction.EDIT, DevicePostureAction.REMOVE],
    },
  },
  emits: ["action-click"],
  setup(props, { emit }) {
    const expanded = ref<string[]>([]);
    const windowsSettingsLabeledExpandButton = ref();
    const macOsSettingsLabeledExpandButton = ref();

    const icons: Record<string, string> = {
      [DeviceVulnerability.UAC_NOTIFICATIONS_MISSING]: "$vulnerability",
      [DeviceVulnerability.DEVICE_PASSWORD_MISSING]: "$listAuthentication",
      [DeviceVulnerability.FIREWALL_DISABLED]: "$endpointVulnerability",
      [DeviceVulnerability.ENCRYPTION_DISABLED]: "$deviceEncryption",
      [DeviceVulnerability.DEVELOPMENT_MODE_ENABLED]: "$developmentMode",
      [DeviceVulnerability.NON_GENUINE_WINDOWS]: "$nonGenuineWindows",
      [DevicePostureSettings.WIFI]: "$wifiS",
      [DevicePostureSettings.DLP_SCAN_SCHEDULE]: "$deviceEncryption",
      [DevicePostureSettings.USER_ACCOUNT_POLICY]: "$dataS",
      [DeviceVulnerability.GATEKEEPER_UNAVAILABLE]: "$firewallDisabled",
      [DeviceVulnerability.APPLE_MOBILE_FILE_INTEGRITY_UNAVAILABLE]: "$firewallDisabled",
      [DeviceVulnerability.SYSTEM_INTEGRITY_PROTECTION_UNAVAILABLE]: "$firewallDisabled",
      [DevicePostureSettings.USB_LOCKDOWN]: "$lock",
    };

    const getPolicyTypeIcon = (policyType: DeviceVulnerability | DevicePostureSettings): string => {
      return icons[policyType];
    };

    const getPostureTextClass = (item: DevicePostureCombinedType) => {
      const data = (item as DevicePostureRegularItem).postureValue;

      if (!data) {
        return {};
      }

      return {
        "text-error": data === DevicePostureChoice.ENFORCE,
        "text-indigo-medium": data === DevicePostureChoice.REVIEW,
      };
    };

    const expandGroup = (items: DevicePostureItemsTuple) => {
      // if already expanded, then filter out the group items from expanded items
      // if not - then add only unique values because separate items could be expanded before
      const mappedItems: string[] = items.map(({ raw }) => raw.tableItemId);

      expanded.value = isExpanded(mappedItems)
        ? expanded.value.filter((v) => !mappedItems.includes(v))
        : union(expanded.value, mappedItems);
    };

    const isExpanded = (items: string[]) => {
      return expanded.value.length ? difference(items, expanded.value).length === 0 : false;
    };

    const isGroupExpanded = (items: DevicePostureItemsTuple) => {
      const mappedItems: string[] = items.map(({ raw }) => raw.tableItemId);
      return isExpanded(mappedItems);
    };

    const showExpandGroup = (item: DevicePostureAdaptedItem) => {
      return !!item.policiesQuantity;
    };

    const onActionClick = (
      action: DevicePostureAction,
      policy: DevicePostureCombinedType,
      item: DevicePostureAdaptedItem
    ) => {
      emit("action-click", { action, policy, item });
    };

    onMounted(() => {
      if (windowsSettingsLabeledExpandButton.value) {
        windowsSettingsLabeledExpandButton.value.$el.click();
      }

      if (macOsSettingsLabeledExpandButton.value) {
        macOsSettingsLabeledExpandButton.value.$el.click();
      }
    });

    return {
      expanded,
      windowsSettingsLabeledExpandButton,
      macOsSettingsLabeledExpandButton,
      getPolicyTypeIcon,
      getPostureTextClass,
      expandGroup,
      isGroupExpanded,
      showExpandGroup,
      onActionClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.label-container {
  gap: 8px;
}

.label-item {
  display: inline-block;
  overflow-wrap: initial;
}

.group-expand-icon {
  .icon {
    background-color: rgb(var(--v-theme-primary));
    border-radius: 50%;

    &::before {
      color: rgb(var(--v-theme-white)) !important;
    }
  }
}
.group-header {
  background: rgb(var(--v-theme-indigo-pale)) !important;
  border-bottom: 0 !important;
}

.v-btn--icon {
  opacity: 1;
}

:deep(*) {
  .v-data-table-footer {
    display: none;
  }

  .v-data-table tr {
    height: 56px;
  }

  .v-select__selections {
    justify-content: flex-end;
  }

  .v-select__selections input {
    display: none !important;
  }
}
</style>
